import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';
import { cn } from './cn';

/**
 * Input — Design System v2 (docs/design-system-v2.html §5.input).
 *
 * Aceita `leftIcon` / `rightIcon` (qualquer ReactNode — geralmente um ícone
 * `@tabler/icons-react`). O slot direito é absoluto (não empurra padding);
 * use-o p/ ícones decorativos OU clicáveis (basta dar onClick em quem
 * passa). NÃO faz contador — esse é trabalho do {@link Field}.
 */

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
}

// Altura 44px segue exatamente `docs/design/login_mockup.html` §.field-wrap
// input. Trocar aqui propaga para todas as telas usando o primitivo.
const BASE =
  'w-full h-11 rounded-md border border-border-strong bg-bg-surface-v2 px-[14px] font-body text-[14px] text-text-primary placeholder:text-text-faint-v2 transition-all focus:outline-none focus:border-primary focus:shadow-ring disabled:opacity-60 disabled:cursor-not-allowed';

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { leftIcon, rightIcon, className, ...rest },
  ref,
) {
  if (!leftIcon && !rightIcon) {
    return <input ref={ref} className={cn(BASE, className)} {...rest} />;
  }
  return (
    <div className="relative">
      {leftIcon && (
        <span
          aria-hidden="true"
          className="absolute left-[13px] top-1/2 -translate-y-1/2 text-text-faint-v2 pointer-events-none"
        >
          {leftIcon}
        </span>
      )}
      <input
        ref={ref}
        className={cn(BASE, leftIcon ? 'pl-10' : null, rightIcon ? 'pr-10' : null, className)}
        {...rest}
      />
      {rightIcon && (
        <span className="absolute right-[13px] top-1/2 -translate-y-1/2 text-text-faint-v2">
          {rightIcon}
        </span>
      )}
    </div>
  );
});
